// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {ApolloQueryResult} from 'apollo-client';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Redirect} from 'react-router';
// @todo: Migrate to 'Link' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Link, useHistory} from 'react-router-dom';
import {CellInfo} from 'react-table';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {BackLink} from '../components/BackLink';
import {SelectEntity} from '../components/Launch/Queues/SelectEntity';
import {TeamMemberRole} from '../components/MembersTable/TeamMembersTable/utils';
import {DEFAULT_PROJECT_NAME} from '../components/ModelRegistry/utils';
import {ProtectedAliases} from '../components/Registries/common/ProtectedAliases';
import {MigrationStatusBanner} from '../components/Registries/ModelRegistryMigration/MigrationStatusBanner';
import {ModelRegistryMigrationContext} from '../components/Registries/ModelRegistryMigration/ModelRegistryMigrationContextProvider';
import {MigrationStatus} from '../components/Registries/ModelRegistryMigration/utils';
import {SemanticLoader as Loader} from '../components/utility/InstrumentedLoader';
import WBReactTable from '../components/WBReactTable';
import * as Generated from '../generated/graphql';
import {WorkflowsAdminType} from '../generated/graphql';
import {WorkflowsAdminUserData} from '../graphql/teamQuery';
import {useViewer} from '../state/viewer/hooks';
import {setDocumentTitle} from '../util/document';
import {propagateErrorsContext} from '../util/errors';
import {trackModelRegistryAccessControlsInteraction} from '../util/navigation';
import * as urls from '../util/urls';
import * as S from './EntityRegistryAccessControlsPage.styles';

export const EntityRegistryAccessControlsPage = (props: {
  entityName: string;
}) => {
  const history = useHistory();
  // Use the viewer's default entity
  const viewer = useViewer();
  const [entityName, setEntityName] = useState(props.entityName);
  const {
    data: teamPageQuery,
    loading,
    refetch,
  } = Generated.useTeamPageQuery({
    skip: entityName === '',
    variables: {
      entityName,
      workflowsAdminType: WorkflowsAdminType.Model,
    },
  });
  useEffect(() => {
    setDocumentTitle('Access controls');
  }, []);

  // Model Registry Migration Context
  const {
    entityMigrationStatus: MRMigrationStatus,
    isMRReadOnly: readOnlyBasedOnMigrationStatus,
  } = useContext(ModelRegistryMigrationContext);

  if (loading) {
    return <Loader active name="entity-registry-access-controls-page-loader" />;
  }
  const viewerTeams =
    viewer?.teams?.edges?.filter(edge => edge.node?.isTeam) ?? [];

  const workflowsAdmins = teamPageQuery?.entity?.workflowsAdmins ?? [];
  // Grab registry admins
  const adminsMap: Record<string, WorkflowsAdminUserData> = {};
  for (const a of workflowsAdmins) {
    if (a.email == null) {
      continue;
    }
    adminsMap[a.email] = {
      email: a.email,
      username: a.username ?? '',
      name: a.name,
      id: a.id ?? '',
    };
  }
  const teamAdmins = teamPageQuery?.entity?.members.filter(
    m => m.role === TeamMemberRole.ADMIN
  );

  for (const a of teamAdmins ?? []) {
    if (a.email == null) {
      continue;
    }
    adminsMap[a.email] = {
      email: a.email,
      username: a.username ?? '',
      name: a.name,
      id: a.id ?? '',
    };
  }
  const registryAdmins = Object.values(adminsMap);
  const isViewerRegistryAdmin = registryAdmins.some(
    admin => admin.username === (viewer?.username || '')
  );
  const canEditAccessControls =
    !readOnlyBasedOnMigrationStatus && isViewerRegistryAdmin;

  // Access controls exist only for teams so for personal users, redirect below
  // to the model registry homepage
  const isPersonal = viewer?.username === entityName;
  if (isPersonal) {
    toast(
      'Access controls for the model registry are only available to teams.'
    );
    return (
      <Redirect to={urls.objectRegistry('model', undefined, entityName)} />
    );
  }
  return (
    <div>
      <S.RegistryLinkContainer>
        <BackLink
          to={urls.objectRegistry('model', undefined, entityName)}
          text="Back to model registry"
        />
      </S.RegistryLinkContainer>
      <S.AccessControlsPageHeader>
        {viewerTeams.length === 0 ? (
          <div>
            Access controls for the model registry are only available to teams.
          </div>
        ) : (
          <>
            <div>
              <h3>Model Registry Access Controls for &nbsp;&nbsp;</h3>
            </div>
            <SelectEntity
              size="large"
              value={entityName}
              includePersonalEntity={false}
              onSelectEntity={(name: string) => {
                history.push(urls.registryAccessControls(name, 'model'));
                setEntityName(name);
              }}
            />
          </>
        )}
      </S.AccessControlsPageHeader>
      <MigrationStatusBanner
        className="mb-24"
        entityName={entityName}
        projectName={DEFAULT_PROJECT_NAME}
      />
      {MRMigrationStatus !== MigrationStatus.COMPLETED && (
        <div>
          <ModelRegistryAdmins
            entityName={entityName}
            admins={registryAdmins}
            isViewerRegistryAdmin={canEditAccessControls}
          />
          <MRProtectedAliases
            aliases={teamPageQuery?.entity?.protectedAliases ?? []}
            admins={registryAdmins}
            entityName={entityName}
            refetch={refetch}
            isViewerRegistryAdmin={canEditAccessControls}
          />
        </div>
      )}
    </div>
  );
};

type ModelRegistryAdminsProps = {
  entityName: string;
  admins: WorkflowsAdminUserData[];
  isViewerRegistryAdmin: boolean;
};

export const ModelRegistryAdmins = ({
  entityName,
  admins,
  isViewerRegistryAdmin,
}: ModelRegistryAdminsProps) => {
  const history = useHistory();
  if (entityName === '') {
    return <RegistryAdminsCopy />;
  }

  return (
    <S.RegistryAdminsContainer>
      <RegistryAdminsCopy />
      <Button
        size="large"
        variant="primary"
        icon="forward-next"
        disabled={!isViewerRegistryAdmin}
        onClick={() => {
          const url = `/${entityName}/members`;
          history.push(url);
        }}>
        Manage registry admins
      </Button>
      <RegistryAdminsTable admins={admins} />
    </S.RegistryAdminsContainer>
  );
};

const RegistryAdminsCopy = () => {
  return (
    <>
      <S.RegistryAdminsCopyHeader>
        <div>Registry Admins </div>
        <Tooltip
          position="right center"
          trigger={<Icon style={{marginLeft: '3px'}} name="info" />}>
          Registry admins can perform any actions involving protected aliases.
        </Tooltip>
      </S.RegistryAdminsCopyHeader>
      <div>
        <S.RegistryAdminsCopyText>
          Team admins or current registry admins can manage the list of registry
          admins by visiting the relevant team's
          <b> Members</b> page.
        </S.RegistryAdminsCopyText>
      </div>
    </>
  );
};

type ModelRegistryAdminsTableProps = {
  admins: WorkflowsAdminUserData[];
};
export const RegistryAdminsTable: React.FC<ModelRegistryAdminsTableProps> = ({
  admins,
}) => {
  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'Name',
        accessor: 'name',
        minWidth: 150,
      },
      {
        Header: 'Username',
        accessor: 'username',
        minWidth: 150,
        Cell: (cellInfo: CellInfo) => {
          const username = cellInfo.value;
          return (
            <Tooltip
              position="top center"
              content="Click to view profile"
              trigger={
                <Link to={`/${encodeURIComponent(username)}`}>{username}</Link>
              }
            />
          );
        },
      },
      {
        Header: 'Email',
        accessor: 'email',
        minWidth: 200,
      },
    ];

    return baseColumns;
  }, []);

  const rows = useMemo(() => {
    return admins.map(admin => {
      return {
        searchString: `${admin.name}|${admin.username}|${admin.email}`,
        row: {
          name: admin.name,
          username: admin.username,
          email: admin.email,
        },
      };
    });
  }, [admins]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div style={{marginTop: '20px'}}>
      <WBReactTable data={rows} columns={columns} />
    </div>
  );
};

type MRProtectedAliasesProps = {
  aliases: string[];
  entityName: string;
  refetch: () => Promise<ApolloQueryResult<any>>;
  admins: WorkflowsAdminUserData[];
  isViewerRegistryAdmin: boolean;
};

export const MRProtectedAliases: React.FC<MRProtectedAliasesProps> = ({
  aliases,
  entityName,
  refetch,
  admins,
  isViewerRegistryAdmin,
}) => {
  const [addAliases] = Generated.useAddProtectedAliasesMutation({
    context: propagateErrorsContext(),
  });
  const [deleteAliases] = Generated.useRemoveProtectedAliasesMutation({
    context: propagateErrorsContext(),
  });

  const onAddAlias = React.useCallback(
    async (tag: Generated.Tag) => {
      if (!isViewerRegistryAdmin) {
        toast('You must be a registry admin to add protected aliases.');
        return;
      }
      try {
        await addAliases({
          variables: {
            entityName,
            aliases: [tag.name],
            adminType: WorkflowsAdminType.Model,
          },
        });
        trackModelRegistryAccessControlsInteraction({
          event: 'add_protected_alias',
        });
        await refetch();
        toast('Successfully added protected alias.');
      } catch (e) {
        console.error(e);
        toast('Something went wrong when adding this protected alias.');
      }
    },
    [addAliases, entityName, isViewerRegistryAdmin, refetch]
  );

  const onDeleteAlias = React.useCallback(
    async (alias: string) => {
      if (!isViewerRegistryAdmin) {
        toast('You must be a registry admin to delete protected aliases.');
        return;
      }
      try {
        await deleteAliases({
          variables: {
            entityName,
            aliases: [alias],
            adminType: WorkflowsAdminType.Model,
          },
        });
        await refetch();
        toast('Successfully deleted protected alias');
      } catch (e) {
        console.error(e);
        toast('Something went wrong when deleting this protected alias');
      }
    },
    [deleteAliases, entityName, isViewerRegistryAdmin, refetch]
  );

  return (
    <S.ProtectedAliasesContainer>
      <ProtectedAliases
        aliases={aliases}
        onAddAlias={onAddAlias}
        onDeleteAlias={onDeleteAlias}
        isViewerRegistryAdmin={isViewerRegistryAdmin}
        isGlobalRegistry={false}
      />
    </S.ProtectedAliasesContainer>
  );
};

export default EntityRegistryAccessControlsPage;
